import * as React from 'react'
import { useSelector } from 'react-redux';
import { WizardHeader } from '../';
import { newBWICActions, submitActions as actions } from '../../../actions';
import { roles } from '../../../constants';
import { RequestState } from '../../../constants/request-state';
import { useSettlementAgentAgreements } from '../../../effects/useSettlementAgentAgreements';
import { UserSlimWithRoles } from '../../../types/account/UserSlimWithRoles';
import { SettlementAgreementStatus } from '../../../types/bid-as-dealer/SettlementAgreementStatus';
import { Company } from '../../../types/company/Company';
import { BwicProcessType } from '../../../types/models/Process';
import { Security } from '../../../types/security/Security';
import { AppState } from '../../../types/state/AppState';
import { GridDataItem } from '../../../types/state/GridState';
import { user } from '../../../user';
import { formatUtils, isRequesting, isRequestSuccess } from '../../../utils';
import { Confirm } from '../../alerts';
import { Preloader } from '../../common';
import { TradingProtocol } from '../../common/TradingProtocol';
import { Disclaimer, Participants, Rules, Securities } from '../../submit';
import { BwicDateTime } from '../../submit/BwicDateTime';
import { useAppDispatch } from '../../../effects/useAppDispatch';

export function Submit() {
    const dispatch = useAppDispatch();
    const [directBwicConfirmationVisible, setDirectBwicConfirmationVisible] = React.useState(false);
    const agreementsState = useSettlementAgentAgreements();
    const isDirectBwic = useSelector((s: AppState) =>
        isRequestSuccess(agreementsState.requestState) &&
        s.entities.brokerDealerList.items.some(d =>
            d.isClearingBank &&
            s.newBwicDealers.selectedDealers[d.id] &&
            agreementsState.items.some(a => a.settlementAgent.id === d.id && a.agreementStatus === SettlementAgreementStatus.confirmed))
    );
    const dataItems = useSelector((s: AppState) => s.grid.dataItems);
    const positions: GridDataItem<Security>[] = dataItems.filter((i: GridDataItem<Security>) => !i.draft);
    const userCompany: Company = useSelector((s: AppState) => s.submit.userCompany);
    const submitState: RequestState = useSelector((s: AppState) => s.submit.submitState);
    const process: any = useSelector((s: AppState) => s.process);
    const template = useSelector((s: AppState) => s.process.templates.current);
    const isAllToAll =
        useSelector((state: AppState) => state.process.isAllToAll) ||
        user.hasRoles(roles.BrokerDealerTrader);

    const seller = user.current()!;
    const isLoading = isRequesting(submitState) || !userCompany;

    React.useEffect(() => {
        dispatch(actions.init());
    }, [dispatch]);

    const handleSubmit = () => {
        if (isDirectBwic) {
            setDirectBwicConfirmationVisible(true);
        } else {
            dispatch(newBWICActions.submit());
        }
    }

    const handleSubmitConfirmed = () => {
        setDirectBwicConfirmationVisible(false);
        dispatch(newBWICActions.submit());
    }

    const formatTitle = () => {
        const amount = positions.reduce((a, b) => +a + +b.size, 0);

        return formatUtils.formatBwicTitle(
            amount,
            positions.map(s => s.currency),
            positions.map(s => s.rating));
    }

    return (
        <>
            <div className="sub-header">
                <WizardHeader
                    isStepNavigationDisabled={isLoading || isRequestSuccess(submitState)}
                    onSubmit={handleSubmit}
                />
            </div>

            <div className="container-step-submit container-sidebar container-flex">
                {!isRequestSuccess(submitState) && (
                    <Preloader inProgress={isLoading}>
                        {
                            !isLoading &&
                            <>
                                <div className="sidebar flex-item-sidebar container-flex">
                                    <Participants />
                                </div>
                                <div className="flex-item-template content-part-sidebar container-flex">
                                    <h2 className="template-title">Email Preview</h2>
                                    <div className="template-wrapper">
                                        <div className="template-cnt">
                                            <EmailDisclaimer users={userCompany.users} isAllToAll={isAllToAll} />
                                            {!isAllToAll && <h2>{userCompany.name}</h2>}
                                            <p>
                                                <>
                                                    {template?.type === BwicProcessType.Live && <>LiveBidding&#8482; </>}
                                                    New BWIC: {formatTitle()} due
                                                    <BwicDateTime
                                                        bidsDue={process.bidsDue}
                                                        date={process.date}
                                                        timeZone={process.timeZone}
                                                    />
                                                </>
                                            </p>
                                            <Securities />
                                            {
                                                (isAllToAll || isDirectBwic) &&
                                                <h2 className="trading-protocol">
                                                    <TradingProtocol isAllToAll={isAllToAll} isDirectBidding={isDirectBwic} />
                                                </h2>
                                            }
                                            <Rules />
                                            <p className="regards">
                                                <strong>Regards,</strong>
                                                {
                                                    isAllToAll
                                                        ? 'Team KopenTech'
                                                        : `${seller.firstName} ${seller.lastName}`
                                                }
                                            </p>
                                            <p className="margin-none">Powered by:</p>
                                            <i className="icon icon-logo-bwic" />
                                            <Disclaimer />
                                        </div>
                                    </div>
                                </div>
                                {
                                    directBwicConfirmationVisible &&
                                    <Confirm
                                        text="The BWIC doesn’t contain CLO securities that require physical settlement."
                                        onCancel={() => setDirectBwicConfirmationVisible(false)}
                                        onConfirm={handleSubmitConfirmed}
                                    />
                                }
                            </>
                        }
                    </Preloader>
                )}
            </div>
        </>
    );
}

function EmailDisclaimer({ users, isAllToAll }: { users: UserSlimWithRoles[], isAllToAll: boolean | null }) {
    if (user.hasRoles(roles.BrokerDealerTrader) || isAllToAll) {
        return <div className="email-disclaimer">
            <h4>This is an auto-generated message sent by the KTX ATS Platform.</h4>
            <p>Please do not reply. To bid, login to the platform.</p>
        </div>

    } else {
        return <div className="email-disclaimer">
            <h4>This is an auto-generated message sent by the KTX ATS Platform.</h4>
            <p>Please do not reply.</p>
            <p>To bid, login to the platform or contact the seller:</p>
            <table>
                <tbody>
                    {
                        users.filter(u => u.roles.some(r => r === roles.SellerTrader)).map(m => (
                            <tr key={m.id}>
                                <th>{m.firstName} {m.lastName}</th>
                                <td><span className="email">{m.email}</span></td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>
        </div>
    }
}
